import { Observable } from 'rxjs'
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { stringify } from 'zipson'
import { GlobalState } from 'store'
import { GridActions, GridActionTypes } from 'store/grid'
import { CrudActions } from 'api'
import { penultimate } from '../utils'
import { GlobalGridSettings } from '../store/grid/gridTypes'
import { SubscriptionFilterConfig } from 'model'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { isCurrentOrganisationBuySideSelector } from 'selectors/organisationSelectors'
import { BTQAction } from 'epics'
import { updateSubscriptions$ } from './epicUtils'

type UserSettingsState = ReturnType<typeof getUserSettingsState>

/**
 * This function prepares the GridStore data for persistence on the server
 * The data is encoded using the zipson (https://github.com/jgranstrom/zipson) library which compresses
 * JSON to a much terser format. This is required to stay within the message limit in the server
 */
function getUserSettingsState([_, state]: [any, GlobalState]) {
  const { connectionStore, repositoryStore, gridStore } = state
  const { id } = connectionStore.authorisedUser!
  const userSettings = repositoryStore.USER_SETTINGS[id]
  return {
    id,
    userSettings,
    payload: stringify(gridStore, { fullPrecisionFloats: true })
  }
}

function filterValidUpdate(userSettingsState: UserSettingsState): boolean {
  return (
    !!userSettingsState.id &&
    !!userSettingsState.userSettings &&
    userSettingsState.userSettings.lastUpdate.entity.payload !== userSettingsState.payload
  )
}

function getUserSettingsUpsertAction({ id, userSettings, payload }: UserSettingsState) {
  const {
    lastUpdate: {
      entity: { version }
    }
  } = userSettings
  const update = {
    id,
    version,
    payload
  }
  return CrudActions.userSettingsUpsert(update)
}

export function saveGridSettingsEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>): Observable<BTQAction> {
  return action$.pipe(
    ofType<
      BTQAction,
      | GridActionTypes.SAVE_COLUMN_SETTINGS
      | GridActionTypes.SAVE_COLUMN_COPY_SETTINGS
      | GridActionTypes.SAVE_BULK_DATE_UPDATE_FIELDS
      | GridActionTypes.SAVE_GLOBAL_GRID_SETTINGS
      | GridActionTypes.SAVE_COLLAPSED_ROW_IDS
      | GridActionTypes.SAVE_SELECTED_ITEM_IDS,
      any
    >(
      GridActionTypes.SAVE_COLUMN_SETTINGS,
      GridActionTypes.SAVE_COLUMN_COPY_SETTINGS,
      GridActionTypes.SAVE_BULK_DATE_UPDATE_FIELDS,
      GridActionTypes.SAVE_GLOBAL_GRID_SETTINGS,
      GridActionTypes.SAVE_COLLAPSED_ROW_IDS,
      GridActionTypes.SAVE_SELECTED_ITEM_IDS
    ),
    withLatestFrom(state$),
    map(getUserSettingsState),
    filter(filterValidUpdate),
    map(getUserSettingsUpsertAction)
  )
}

export type SaveGlobalGridSettingsAction = ReturnType<typeof GridActions.saveGlobalGridSettings>

export function gridSettingsModifyActiveSubscriptionsEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, GridActionTypes.SAVE_GLOBAL_GRID_SETTINGS, SaveGlobalGridSettingsAction>(GridActionTypes.SAVE_GLOBAL_GRID_SETTINGS),
    map(action => action.payload.globalGridSettings),
    withLatestFrom(state$.pipe(penultimate())),
    filter(([globalGridSettings, oldState]: [GlobalGridSettings, GlobalState]) => {
      const updatedGlobalGridSettings = { ...oldState.gridStore.global, ...globalGridSettings }
      const oldShowTerminatedRepos = oldState.gridStore.global.showTerminatedRepos
      const oldShowOrgActivity = oldState.gridStore.global.showOrgActivity
      const oldShowOnlyUsersSalesActivity = oldState.gridStore.global.showOnlyUsersSalesActivity
      const oldHideExpiredRejected = oldState.gridStore.global.hideExpiredRejectedInInitiated
      const oldAxeShowCancelled = oldState.gridStore.global.showCancelledAxes
      const oldshowDraftMapping = oldState.gridStore.global.showDraftMapping
      return (
        oldShowTerminatedRepos !== updatedGlobalGridSettings.showTerminatedRepos ||
        oldShowOrgActivity !== updatedGlobalGridSettings.showOrgActivity ||
        oldShowOnlyUsersSalesActivity !== updatedGlobalGridSettings.showOnlyUsersSalesActivity ||
        oldHideExpiredRejected !== updatedGlobalGridSettings.hideExpiredRejectedInInitiated ||
        oldAxeShowCancelled !== updatedGlobalGridSettings.showCancelledAxes ||
        oldshowDraftMapping !== updatedGlobalGridSettings.showDraftMapping
      )
    }),
    map(([globalGridSettings, oldState]: [GlobalGridSettings, GlobalState]) => ({ ...oldState.gridStore.global, ...globalGridSettings })),
    mergeMap(globalGridSettings => {
      const filterConfig: SubscriptionFilterConfig = {
        globalGridSettings,
        authorisedUser: createAuthorisedUserSelector()(state$.value),
        isBuySide: isCurrentOrganisationBuySideSelector(state$.value)
      }

      return updateSubscriptions$({ filterConfig, state$ })
    })
  )
}

export const gridSettingsEpic = combineEpics(gridSettingsModifyActiveSubscriptionsEpic, saveGridSettingsEpic)
